import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import {
  type AccountSummaryData,
  GET_ACCOUNT_SUMMARY
} from '../queries/accountSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountSummaryData;
}

const ADDRESS_FIELDS: FieldMapping[] = [
  { apiField: 'address1Street1', formField: 'address1Street1' },
  { apiField: 'address1Street2', formField: 'address1Street2' },
  { apiField: 'city', formField: 'city' },
  { apiField: 'stateProvince', formField: 'stateProvince' },
  { apiField: 'zipPostalCode', formField: 'zipPostalCode' },
  { apiField: 'countryRegion', formField: 'countryRegion' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountSummaryData,
  initial: AccountSummaryData
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  for (const { apiField, formField } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    if (currentVal !== initialVal) {
      changes[apiField] = currentVal === '' ? null : currentVal;
    }
  }

  return changes;
}

const UPSERT_PRIMARY_ADDRESS = gql`
  mutation UpsertPrimaryAddress(
    $accountId: ID!
    $values: PrimaryAddressUpsertInput!
  ) {
    upsertPrimaryAddress(accountId: $accountId, values: $values) {
      ok
      created
      primaryAddress {
        id
        accountId {
          id
          name
        }
        address1Street1
        address1Street2
        city
        stateProvince
        zipPostalCode
        countryRegion
      }
    }
  }
`;

export interface EditPrimaryAddressDialogProps {
  accountId: string;
  initialValue: AccountSummaryData;
  isOpen: boolean;
  onChange: (data: AccountSummaryData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountSummaryData;
}

export function EditPrimaryAddressDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditPrimaryAddressDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [upsertPrimaryAddress] = useMutation(UPSERT_PRIMARY_ADDRESS, {
    refetchQueries: [
      { query: GET_ACCOUNT_SUMMARY, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(ADDRESS_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof AccountSummaryData>(
    field: K,
    fieldValue: AccountSummaryData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertPrimaryAddress({
        variables: { accountId, values: changes }
      });

      const data = response.data as {
        upsertPrimaryAddress?: { ok?: boolean };
      };

      if (data.upsertPrimaryAddress?.ok === false) {
        toast.error('Failed to save primary address');
      } else {
        toast.success('Primary address saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving primary address:', error);
      toast.error('Failed to save primary address');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Primary Address"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <EditFormField
          label="Street 1"
          onChange={v => {
            updateField('address1Street1', v);
          }}
          value={value.address1Street1}
        />
        <EditFormField
          label="Street 2"
          onChange={v => {
            updateField('address1Street2', v);
          }}
          value={value.address1Street2}
        />
        <EditFormField
          label="City"
          onChange={v => {
            updateField('city', v);
          }}
          value={value.city}
        />
        <EditFormField
          label="State/Province"
          onChange={v => {
            updateField('stateProvince', v);
          }}
          value={value.stateProvince}
        />
        <EditFormField
          label="ZIP/Postal Code"
          onChange={v => {
            updateField('zipPostalCode', v);
          }}
          value={value.zipPostalCode}
        />
        <EditFormField
          label="Country/Region"
          onChange={v => {
            updateField('countryRegion', v);
          }}
          value={value.countryRegion}
        />
      </div>
    </EditDialog>
  );
}
